import type { ReactNode } from 'react';
import * as adminApi from '../admin/adminApi.ts';
import type { AdminHealth } from '../admin/adminApi.ts';
import { DISK_STATE_KEY, diskNote, diskState, formatBytes } from '../admin/derive.ts';
import { AdminPage, LoadState, useLoaded } from '../components/AdminPage.tsx';
import type { RunStatus } from '../types.ts';

const STATUS_ORDER: RunStatus[] = ['queued', 'running', 'done', 'failed', 'created'];

/**
 * `#/admin/health` (docs/specs/accounts.md, sections 6 to 8): a plain readout. It reads once, and
 * again when asked. It does not poll: a health page that repaints by itself is a page nobody can
 * quote a figure from.
 */
export function AdminHealthView(): JSX.Element {
  const { state, inFlight, reload } = useLoaded(adminApi.getHealth);
  return (
    <AdminPage title="Health">
      {state.status !== 'ready' ? (
        <LoadState state={state} inFlight={inFlight} onRetry={reload} />
      ) : (
        <>
          <HealthReadout health={state.data} />
          {/* The server scans every run file for these figures, so one read at a time: the hook
              ignores a reload while a request is in flight, and the button says so. */}
          <button className="btn admin-refresh" type="button" disabled={inFlight} onClick={reload}>
            {inFlight ? 'Reading…' : 'Read again'}
          </button>
        </>
      )}
    </AdminPage>
  );
}

function Row({ term, note, children }: { term: string; note?: string | null; children: ReactNode }): JSX.Element {
  return (
    <div className="readout-row">
      <dt>{term}</dt>
      <dd>{children}</dd>
      {note && <dd className="readout-note">{note}</dd>}
    </div>
  );
}

/** The readout, given the figures: apart from the loading above so that a test renders it from data. */
export function HealthReadout({ health }: { health: AdminHealth }): JSX.Element {
  const disk = diskState(health.disk);
  const workerOk = health.worker === 'ok';
  return (
    <>
      <section className="admin-section">
        <div className="tick">
          <span className="label">§ Worker and queue</span>
        </div>
        <dl className="readout">
          <Row
            term="Worker"
            note={workerOk ? null : 'No worker has reported in time. A queued run waits until a worker starts.'}
          >
            <span className={`key ${workerOk ? 'k-present' : 'k-absent'}`}>{health.worker}</span>
          </Row>
          <Row term="Runs waiting in the queue">{health.queue.pending}</Row>
          <Row term="Runs claimed by a worker">{health.queue.claimed}</Row>
        </dl>
      </section>

      <section className="admin-section">
        <div className="tick">
          <span className="label">§ Disk</span>
        </div>
        <dl className="readout">
          <Row term="Free space on the data volume" note={diskNote(disk)}>
            {health.disk.free_bytes == null ? 'not readable' : formatBytes(health.disk.free_bytes)}
            <span className={`key ${DISK_STATE_KEY[disk]}`}>{disk}</span>
          </Row>
          <Row term="Launch floor">{formatBytes(health.disk.min_free_bytes)}</Row>
        </dl>
      </section>

      <section className="admin-section">
        <div className="tick">
          <span className="label">§ Runs</span>
        </div>
        <dl className="readout">
          <Row term="Runs on disk">{health.runs.total}</Row>
          {STATUS_ORDER.map((status) => (
            <Row key={status} term={`Status: ${status}`}>
              {health.runs.by_status[status] ?? 0}
            </Row>
          ))}
          <Row term="Refunds due">{health.runs.refund_due}</Row>
          <Row term="Over-cost runs">{health.runs.over_cost}</Row>
        </dl>
        <p className="muted admin-footnote">
          The <a href="#/admin/runs">runs page</a> marks each over-cost run and each refund.
        </p>
      </section>
    </>
  );
}
